/**
 * What the customer is actually asked, in order: the question, how the answer is
 * collected, and where each answer leads. Transcribed from the action state
 * machine in `backend/insure/services/insurance/actions/`.
 *
 * Deliberately not a mirror of the backend: polling states, spinners and steps
 * with no UI are left out, and branch labels are written as the customer
 * experiences them. The mechanics behind a branch live in `behindTheScenes`,
 * which the diagram keeps off the canvas and shows only in the detail panel.
 */

export enum EdgeKind {
  /** Something the customer picks, types, or submits. */
  ANSWER = 'ANSWER',
  /** What comes back — including a question that gets asked again. */
  OUTCOME = 'OUTCOME',
}

export interface Edge {
  kind: EdgeKind
  label: string
  /** Service the flow lands on next, or `END`. */
  to: string
}

export interface Bubble {
  /** Which situation produces this wording; omitted when there's only one. */
  when?: string
  text: string
  /** Written here rather than quoted from the product — exempt from the copy check. */
  editorial?: boolean
}

export interface Step {
  /** `InsuranceApplicationService` member — the shared name for this question. */
  service: string
  /** How the answer is collected, in plain language. */
  collected: string
  /** The selectable values, where the answer is a choice from a fixed set. */
  options?: string[]
  /** The chat component that renders it. */
  component: string
  asks: Bubble[]
  /** Why the customer might not see this question at all. */
  onlyWhen?: string
  /** Backend mechanics — kept off the canvas on purpose. */
  behindTheScenes?: string
  edges: Edge[]
}

export interface Stage {
  name: string
  /** Action class in `insure/services/insurance/actions/`. */
  action: string
  steps: Step[]
}

/**
 * What the card shows. Bubbles with no `when` are consecutive messages the
 * customer gets in a row, so all of them belong on the card; bubbles with a
 * `when` are alternative wordings, so only the first stands in for the rest.
 */
export function cardQuestion(step: Step): string[] {
  const consecutive = step.asks.filter((ask) => !ask.when)
  if (consecutive.length) return consecutive.map((ask) => ask.text)
  if (step.asks.length) return [step.asks[0].text]
  return [step.collected]
}

const answer = (label: string, to: string): Edge => ({ kind: EdgeKind.ANSWER, label, to })
const outcome = (label: string, to: string): Edge => ({ kind: EdgeKind.OUTCOME, label, to })

export const TERMINAL = 'END'

export const STAGES: Stage[] = [
  {
    name: 'DOT number',
    action: 'DotNumberAction',
    steps: [
      {
        service: 'ADD_DOT_NUMBER',
        collected: 'DOT number field, or a company-name search',
        component: 'DOT_NUMBER maxLength=8',
        behindTheScenes:
          'Looks the DOT up in CAB and stores the carrier contact, fleet, and any coverage lapse. An anonymous visitor also gets “I don’t have a DOT yet”, linking to the new-authority lead form. “I don’t remember my DOT” searches FMCSA’s census by company name; picking a match submits its DOT.',
        asks: [{ text: 'Let’s start with your DOT number.' }],
        edges: [
          outcome('We find the business', 'CONFIRM_DOT_NUMBER'),
          outcome('We can’t find it — asks again', 'ADD_DOT_NUMBER'),
        ],
      },
      {
        service: 'CONTINUE_DOT_NUMBER_AFTER_AGENT',
        collected: 'Continue button',
        component: 'BUTTON_GROUP',
        onlyWhen: 'A Cai agent already filled in your details',
        behindTheScenes:
          'Rendered whenever the application carries the agent-completed flag, whatever the current step. Continuing asks for any email or phone still missing, then skips the company-info questions and resumes at marital status.',
        asks: [{ text: 'Continue', editorial: true }],
        edges: [
          answer('Continue — contact details still missing', 'ADD_EMAIL'),
          answer('Continue — skips the company-info questions', 'ADD_MARITAL_STATUS'),
        ],
      },
      {
        service: 'CONFIRM_DOT_NUMBER',
        collected: 'Two buttons + correction box',
        component: 'BUTTON_GROUP · TEXTAREA',
        asks: [{ text: 'Does this business info look right?' }],
        edges: [
          answer('Yes, that’s correct', 'ADD_EMAIL'),
          answer('Yes, and CAB shows one driver, one truck', 'CONFIRM_OWNER_OPERATOR'),
          answer('Nope, let’s try a different DOT', 'ADD_DOT_NUMBER'),
          answer('Types a correction — we fix it and re-show', 'CONFIRM_DOT_NUMBER'),
        ],
      },
      {
        service: 'CONFIRM_OWNER_OPERATOR',
        collected: 'Yes / No buttons',
        options: ['Yes', 'No'],
        component: 'BUTTON_GROUP',
        onlyWhen: 'CAB reports exactly one power unit and one driver',
        behindTheScenes:
          'Also asked of a funnel visitor, whose DOT step is otherwise skipped. Yes stores owner_operator, and the drivers and trucks steps then open the Add Driver and Add Truck modals on their own instead of the tables — prefilled with the owner’s answers and the truck CAB imported. Closing a modal saves it like Done. More than one driver or truck on file (an ELD import) falls back to the tables.',
        asks: [{ text: 'I’m seeing one driver, one truck for your fleet. Is that correct?' }],
        edges: [answer('Yes', 'ADD_EMAIL'), answer('No — the usual drivers and trucks tables', 'ADD_EMAIL')],
      },
    ],
  },
  {
    name: 'Contact details',
    action: 'ContactDetailsAction',
    steps: [
      {
        service: 'ADD_EMAIL',
        collected: 'Email field',
        component: 'INPUT type="email"',
        onlyWhen: 'We don’t already have your email',
        behindTheScenes:
          'A visitor from the marketing site’s “Get your quotes” is signed in as a stub with a placeholder email, which counts as missing; their first real email promotes the stub to a real account. Estimates-page visitors arrive with the email they gave, so it is skipped for them.',
        asks: [{ text: 'Can I get your work email address?' }],
        edges: [
          answer('Enters an email', 'ADD_MOBILE_NUMBER'),
          outcome('Already has a Cai account — we email a sign-in link', 'ADD_EMAIL'),
          outcome(
            'Another product’s Cai account holds it — we send that account a code',
            'SIGN_IN_SHARED_ACCOUNT'
          ),
          outcome('Another account holds this DOT — asks for the email on file', 'ADD_EMAIL'),
          answer('Use a different DOT', 'ADD_DOT_NUMBER'),
          outcome('Address is taken or invalid — asks again', 'ADD_EMAIL'),
        ],
      },
      {
        service: 'SIGN_IN_SHARED_ACCOUNT',
        collected: '6-digit code · Resend · Different email',
        component: 'VERIFY_EMAIL',
        onlyWhen: 'The email belongs to a Cai account from another product, such as a FuelApp driver',
        behindTheScenes:
          'The code goes to the contact that account signs in with: its email when proved, else its verified phone. The right code moves the application onto that account and signs this browser in as it; a rep working the chat needs the code from the customer.',
        asks: [
          {
            text: '<email> already has a Cai account. We sent a 6-digit code to <the phone ending 1234> on that account. Enter it below to continue your application with it.',
            editorial: true,
          },
        ],
        edges: [
          answer('Enters the code', 'ADD_MOBILE_NUMBER'),
          outcome('Wrong or expired code', 'SIGN_IN_SHARED_ACCOUNT'),
          answer('Uses a different email', 'ADD_EMAIL'),
        ],
      },
      {
        service: 'ADD_MOBILE_NUMBER',
        collected: 'Phone field + Skip button',
        component: 'INPUT type="tel" · BUTTON_GROUP',
        behindTheScenes:
          'Always asked, prefilled with the number we hold. After the DOT step that is often the carrier’s CAB line, which is frequently out of date.',
        asks: [
          {
            text: 'What phone number should your broker use?',
          },
        ],
        edges: [
          answer('Enters a number', 'COVERAGE_START_DATE'),
          answer('Skip for now', 'COVERAGE_START_DATE'),
          outcome('Number is taken — asks again', 'ADD_MOBILE_NUMBER'),
        ],
      },
    ],
  },
  {
    name: 'Documents',
    action: 'UploadDocumentsAction',
    steps: [
      {
        service: 'UPLOAD_DOCUMENTS',
        collected: 'File upload + Skip button',
        component: 'FILE_UPLOAD multiple · BUTTON_GROUP',
        behindTheScenes:
          'Files go to an extraction agent that fills in trucks, trailers, drivers and loss runs. A spinner shows while it runs; a failed read falls through to the manual questions.',
        asks: [
          {
            text: 'Do you have any documents handy? Upload loss run reports, a prior COI, your current policy, or lists of drivers and trucks, and I’ll fill in the rest of your application for you.',
          },
        ],
        edges: [
          answer('Uploads files', 'UPLOAD_DOCUMENTS_REVIEW'),
          answer('Not right now', 'CONFIRM_ENTITY_TYPE'),
          outcome('We can’t read them', 'CONFIRM_ENTITY_TYPE'),
        ],
      },
      {
        service: 'UPLOAD_DOCUMENTS_REVIEW',
        collected: 'Buttons',
        component: 'BUTTON_GROUP',
        asks: [
          { when: 'We found things', text: 'Here’s what I pulled from your documents: {counts}.' },
          {
            when: 'We found nothing',
            text: 'I went through your documents but couldn’t find new details to add.',
          },
        ],
        edges: [answer('Continue', 'CONFIRM_ENTITY_TYPE'), answer('Upload more files', 'UPLOAD_DOCUMENTS')],
      },
    ],
  },
  {
    name: 'Business type',
    action: 'MotorCarrierDetailsAction',
    steps: [
      {
        service: 'CONFIRM_ENTITY_TYPE',
        collected: 'Cards — pick one',
        options: [
          'Individual',
          'Partnership',
          'Corporation/LLC — preselected when the name contains LLC or INC',
        ],
        component: 'CARD_CHECKBOX singleSelect columns=1',
        behindTheScenes:
          'The estimate is only offered when the ENABLE_EARLY_INDICATION flag is on and the business is in a CoverWhale-eligible state.',
        asks: [{ text: 'What best describes your business type?' }],
        edges: [
          answer('Individual · Partnership · Corporation/LLC', 'CONFIRM_OWNER_INFO'),
          outcome('An early estimate is available', 'OFFER_EARLY_INDICATION'),
        ],
      },
      {
        service: 'OFFER_EARLY_INDICATION',
        collected: 'Two buttons',
        component: 'BUTTON_GROUP',
        onlyWhen: 'We can price a rough estimate this early',
        asks: [
          {
            text: 'Great! We can pull a rough estimate. It won’t be exact, but it gives you a good starting point. Would you like to see your estimated premiums now, or continue with the application?',
          },
        ],
        edges: [
          answer('Get rough estimate', 'SHOW_EARLY_INDICATION'),
          answer('Continue application', 'CONFIRM_OWNER_INFO'),
        ],
      },
      {
        service: 'SHOW_EARLY_INDICATION',
        collected: 'Estimate card + Continue',
        component: 'EARLY_INDICATION',
        behindTheScenes:
          'A loading state polls while the estimate generates, and moves on by itself if it fails.',
        asks: [
          {
            text: 'Here’s a rough look at your estimated costs based on what we have so far. Let’s finish the application to lock in exact numbers from our network of insurance providers.',
          },
        ],
        edges: [answer('Continue', 'CONFIRM_OWNER_INFO')],
      },
    ],
  },
  {
    name: 'Owner',
    action: 'BusinessOwnerDetailsAction',
    steps: [
      {
        service: 'CONFIRM_OWNER_INFO',
        collected: 'Button + correction box',
        component: 'BUTTON_GROUP · TEXTAREA',
        onlyWhen: 'We found owner details to check',
        behindTheScenes:
          'The owner’s name comes from the carrier’s FMCSA filing, but the email and phone are the ones the applicant gave us — the filing only fills a gap. A skipped phone step leaves the phone off the card.',
        asks: [{ text: 'Does this owner information look accurate?' }],
        edges: [
          answer('This is accurate', 'ADD_DOB'),
          answer('Types a correction — we fix it and re-show', 'CONFIRM_OWNER_INFO'),
        ],
      },
      {
        service: 'ADD_DOB',
        collected: 'Date of birth field',
        component: 'INPUT type="dob"',
        onlyWhen: 'We don’t already have the owner’s date of birth',
        asks: [
          { when: 'The owner is in the chat', text: 'What is your date of birth?' },
          { when: 'The owner is someone else', text: 'What is the owner’s ({first name}’s) date of birth?' },
        ],
        edges: [answer('Enters a date', 'ADD_MARITAL_STATUS')],
      },
    ],
  },
  {
    name: 'Marital status',
    action: 'MaritalStatusAction',
    steps: [
      {
        service: 'ADD_MARITAL_STATUS',
        collected: 'Cards — pick one',
        options: ['Single', 'Married', 'Divorced', 'Widowed', 'Separated'],
        component: 'CARD_CHECKBOX singleSelect',
        asks: [
          {
            when: 'The owner is in the chat',
            text: 'What’s your current marital status? Insurance providers ask because it can affect discount eligibility and pricing.',
          },
          {
            when: 'The owner is someone else',
            text: 'What is {first name}’s marital status? Insurance providers ask because it can affect discount eligibility and pricing.',
          },
        ],
        edges: [answer('Picks a status', 'ADD_TRAVEL_DISTANCE')],
      },
    ],
  },
  {
    name: 'Freight',
    action: 'FreightTypeAction',
    steps: [
      {
        service: 'ADD_TRAVEL_DISTANCE',
        collected: 'Cards — pick any',
        options: [
          '🚛 Local (0–50 miles)',
          '🛣 Regional (51–150 miles)',
          '🌎 Extended Regional (151–300 miles)',
          '🗺 Long Haul (300+ miles)',
        ],
        component: 'CARD_CHECKBOX',
        asks: [{ text: 'How far do you typically travel from your home base?' }],
        edges: [answer('Picks a radius', 'ADD_FREIGHT_SPECIFIC_ITEMS')],
      },
      {
        service: 'ADD_FREIGHT_SPECIFIC_ITEMS',
        collected: 'Freight allocation editor',
        component: 'FREIGHT_ALLOCATION',
        behindTheScenes:
          'Saved as entered, with no parsing and no confirm step. Some orgs also reject answers that are too generic.',
        asks: [
          {
            text: 'What specific commodities do you haul? Adding percentages for each type of commodity is especially helpful!',
          },
        ],
        edges: [
          answer('Picks freight types and shares', 'PRE_BIND_ELD_DEVICE_CHECK'),
          outcome('Too vague to price, or over 10 — asks again', 'ADD_FREIGHT_SPECIFIC_ITEMS'),
        ],
      },
      {
        service: 'CONFIRM_FREIGHT_SPECIFIC_ITEMS',
        collected: 'Button + correction box',
        component: 'BUTTON_GROUP · TEXTAREA',
        behindTheScenes:
          'Legacy: only reached by an application answered in the old text box, from a browser that had not reloaded since the editor shipped.',
        asks: [{ text: 'Here’s the estimated breakdown of your freight types. Is this accurate?' }],
        edges: [
          answer('This is accurate', 'PRE_BIND_ELD_DEVICE_CHECK'),
          answer('Types a correction — we re-split and re-show', 'CONFIRM_FREIGHT_SPECIFIC_ITEMS'),
        ],
      },
    ],
  },
  {
    name: 'ELD',
    action: 'EldDeviceAction',
    steps: [
      {
        service: 'PRE_BIND_ELD_DEVICE_CHECK',
        collected: 'Yes / No buttons',
        component: 'BUTTON_GROUP',
        asks: [{ text: 'Do you have ELD devices for any of your trucks?' }],
        edges: [answer('Yes', 'PRE_BIND_ELD_PROVIDER_SELECT'), answer('No', 'PRE_BIND_ADD_TELEMATICS')],
      },
      {
        service: 'PRE_BIND_ELD_PROVIDER_SELECT',
        collected: 'Cards — pick one, or type another',
        options: ['Motive', 'Samsara', 'Geotab', 'Verizon Connect', 'Other — type your own'],
        component: 'CARD_CHECKBOX singleSelect allowOther columns=2',
        asks: [{ text: 'Which ELD provider do you use?' }],
        edges: [answer('Motive · Samsara · Geotab · Verizon Connect · Other', 'PRE_BIND_ADD_TELEMATICS')],
      },
    ],
  },
  {
    name: 'Driver info',
    action: 'PreBindTelematicsAction',
    steps: [
      {
        service: 'PRE_BIND_ADD_TELEMATICS',
        collected: 'Connect button + type-it-in button',
        component: 'ADD_TELEMATICS · BUTTON_GROUP',
        onlyWhen: 'Telematics aren’t connected already',
        behindTheScenes:
          'Choosing to connect parks the chat on a polled wait state until the provider reports back.',
        asks: [
          { text: 'Next, driver info.' },
          {
            text: 'I’m going to need details about each driver’s name, license, hiring date, years of experience, and more.',
          },
          { text: 'How would you like to proceed?' },
        ],
        edges: [answer('I’ll type it in', 'ADD_DRIVERS'), answer('Import from telematics', 'ADD_DRIVERS')],
      },
    ],
  },
  {
    name: 'Drivers',
    action: 'DriversAction',
    steps: [
      {
        service: 'ADD_DRIVERS',
        collected: 'Driver table',
        component: 'ADD_DRIVERS',
        asks: [
          {
            when: 'Telematics connected',
            text: 'Your {provider} is connected! Here are the active drivers I found. Please review and confirm.',
          },
          {
            when: 'Typing it in',
            text: 'No lead-in message — the drivers table is shown on its own.',
            editorial: true,
          },
        ],
        edges: [answer('Confirms the drivers', 'ADD_TRUCKS')],
      },
    ],
  },
  {
    name: 'Equipment',
    action: 'EquipmentsAction',
    steps: [
      {
        service: 'ADD_TRUCKS',
        collected: 'Truck table',
        component: 'ADD_EQUIPMENTS type="trucks"',
        asks: [
          {
            when: 'Telematics connected',
            text: 'Great! Here are the active trucks I found in your {provider} account. Please review so we know the information is accurate and complete.',
          },
          {
            when: 'Typing it in',
            text: 'Great! Time to add information about your trucks. We will need details like VIN, license numbers, make, model, and more.',
          },
          {
            when: 'One driver, one truck',
            text: 'Time to add information about your truck!',
          },
        ],
        edges: [
          outcome('We already know about trailers', 'ADD_TRAILERS'),
          outcome('We don’t know of any trailers', 'ADD_TRAILERS_QUESTION'),
        ],
      },
      {
        service: 'ADD_TRAILERS_QUESTION',
        collected: 'Two buttons',
        component: 'BUTTON_GROUP',
        asks: [{ text: 'And do you have any trailers?' }],
        edges: [
          answer('Enter details or upload file', 'ADD_TRAILERS'),
          answer('I don’t have trailers', 'EARLY_LOSS_RUN'),
        ],
      },
      {
        service: 'ADD_TRAILERS',
        collected: 'Trailer table + Skip button',
        component: 'ADD_EQUIPMENTS type="trailers" · BUTTON_GROUP',
        asks: [
          {
            when: 'Trailers were found',
            text: 'I also found information about trailers in your {provider} account. Please review and confirm it’s complete.',
          },
        ],
        edges: [
          answer('Confirms the trailers', 'EARLY_LOSS_RUN'),
          answer('I don’t have trailers', 'EARLY_LOSS_RUN'),
        ],
      },
    ],
  },
  {
    name: 'Loss runs',
    action: 'EarlyLossRunAction',
    steps: [
      {
        service: 'EARLY_LOSS_RUN',
        collected: 'Yes / No buttons',
        component: 'BUTTON_GROUP',
        onlyWhen: 'We don’t already have a completed loss run report on file',
        asks: [{ text: 'Do you have any prior claims?' }],
        edges: [answer('Yes', 'EARLY_LOSS_RUN_ASK'), answer('No', 'EARLY_LOSS_RUN_ASK')],
      },
      {
        service: 'EARLY_LOSS_RUN_ASK',
        collected: 'Two buttons',
        component: 'BUTTON_GROUP',
        asks: [
          {
            when: 'They have prior claims',
            text: 'Do you have loss runs? Almost all insurance providers require loss runs information before checkout.',
          },
          {
            when: 'They have no prior claims',
            text: 'Wonderful! Do you have loss run documents proving no accidents? For new authorities, it’s fine to skip.',
          },
        ],
        edges: [
          answer('Upload loss run reports', 'EARLY_LOSS_RUN_UPLOAD'),
          answer('Skip for now', 'VERIFY_EMAIL'),
        ],
      },
      {
        service: 'EARLY_LOSS_RUN_UPLOAD',
        collected: 'File upload + Skip button',
        component: 'FILE_UPLOAD multiple · BUTTON_GROUP',
        behindTheScenes:
          'Parsed in the background, which can take minutes. Review now happens in the Confirm-Information modal rather than a step of its own.',
        asks: [
          {
            text: 'No lead-in message — the upload control asks for the reports on its own.',
            editorial: true,
          },
        ],
        edges: [answer('Uploads reports', 'VERIFY_EMAIL'), answer('Skip loss runs', 'VERIFY_EMAIL')],
      },
      {
        service: 'EARLY_LOSS_RUN_FORM',
        collected: 'Claim history form',
        component: 'LOSS_RUN_FORM',
        onlyWhen: 'No longer reached — still handled for applications already mid-flow',
        asks: [],
        edges: [answer('Fills in the history', 'VERIFY_EMAIL')],
      },
      {
        service: 'EARLY_LOSS_RUN_CONFIRM',
        collected: 'Prefilled claim history form',
        component: 'LOSS_RUN_FORM prefilled',
        onlyWhen: 'No longer reached — still handled for applications already mid-flow',
        asks: [],
        edges: [answer('Confirms the history', 'VERIFY_EMAIL')],
      },
    ],
  },
  {
    name: 'Email check',
    action: 'VerifyEmailAction',
    steps: [
      {
        service: 'VERIFY_EMAIL',
        collected: '6-digit code · Resend · Change email',
        component: 'VERIFY_EMAIL',
        onlyWhen: 'We created the account and signed you in — the public DOT funnel, or a rep setting you up',
        behindTheScenes:
          'Skipped outright for anyone who signed themselves up. Blocking, because everything after it is mailed to this address.',
        asks: [
          { text: 'Before we go on, let’s confirm your email.' },
          {
            text: 'We sent a 6-digit code to <your email>. Enter it below so we know your quotes and paperwork reach you.',
            editorial: true,
          },
        ],
        edges: [
          answer('Enters the code', 'EARLY_BOR_AUTHORIZATION'),
          outcome('Wrong or expired code', 'VERIFY_EMAIL'),
          outcome('Corrects a typo in the address', 'VERIFY_EMAIL'),
        ],
      },
    ],
  },
  {
    name: 'Broker authorization',
    action: 'EarlyBorAction',
    steps: [
      {
        service: 'EARLY_BOR_AUTHORIZATION',
        collected: 'Signature + Skip button',
        component: 'SIGN_AGREEMENT · BUTTON_GROUP',
        onlyWhen:
          'The authorization isn’t signed yet — one signed on the dashboard or from a link skips this step',
        behindTheScenes:
          'Signed outside the chat while it waits here, the card shows Signed and Skip for now becomes Continue.',
        asks: [
          {
            text: 'Insurance providers often require a signed document that gives Cai permission to request loss run reports and shop for quotes on your behalf.',
          },
          { text: 'Want to knock this out now?' },
        ],
        edges: [
          answer('Signs it', 'CONFIRM_INFORMATION'),
          answer('Skip for now', 'CONFIRM_INFORMATION'),
          answer('Continue, after signing it outside the chat', 'CONFIRM_INFORMATION'),
        ],
      },
    ],
  },
  {
    name: 'Review',
    action: 'ConfirmInformationAction',
    steps: [
      {
        service: 'CANCELLATION_REASON',
        collected: 'Text box',
        component: 'TEXTAREA',
        onlyWhen: 'FMCSA shows a 2+ year gap in your coverage',
        asks: [
          {
            text: 'According to FMCSA, there’s a 2+ year lapse in your insurance coverage. Please explain why to help minimize your insurance premiums.',
          },
        ],
        edges: [answer('Explains the gap', 'CONFIRM_INFORMATION')],
      },
      {
        service: 'CA_NUMBER',
        collected: 'Text box',
        component: 'TEXTAREA',
        onlyWhen: 'Your business address is in California and we have no CA number on file',
        asks: [
          {
            text: 'Since you’re based in California, we’ll also need your CA number (your carrier ID from the California Highway Patrol) to get you quotes. It looks like CA followed by 6 or 7 digits, e.g. CA1234567.',
          },
        ],
        edges: [answer('Enters a CA number', 'CONFIRM_INFORMATION')],
      },
      {
        service: 'CONFIRM_INFORMATION',
        collected: 'Hands over to the confirm page',
        component: 'LINK',
        behindTheScenes:
          'The review is its own page, /insurance/confirm-info: Intake’s steps and Cai’s certification. Confirming asks the carriers, after an email code for anyone who never proved theirs in the chat.',
        asks: [
          { text: 'Alright!' },
          {
            text: 'Give your application one last look over, fill in anything that’s missing, and confirm everything is accurate and truthful.',
          },
        ],
        edges: [
          answer('Confirms on the confirm page', 'SELECT_INDICATION'),
          outcome('Confirms, with no coverage needs on file', 'COVERAGE_START_DATE'),
          outcome('Something’s missing — the page points at it', 'CONFIRM_INFORMATION'),
        ],
      },
    ],
  },
  {
    name: 'Coverage start',
    action: 'CoverageStartDateAction',
    steps: [
      {
        service: 'COVERAGE_START_DATE',
        collected: 'Date picker',
        component: 'COVERAGE_START_DATE',
        behindTheScenes: 'Defaults to the prior policy’s expiry and only accepts today through 60 days out.',
        asks: [{ text: 'When would you like coverage to start?' }],
        edges: [answer('Picks a date', 'ADD_INSURANCE_COVERAGE')],
      },
    ],
  },
  {
    name: 'Coverage types',
    action: 'InsuranceCoverageAction',
    steps: [
      {
        service: 'ADD_INSURANCE_COVERAGE',
        collected: 'Coverage picker — pick any',
        options: [
          'Auto Liability — always on, cannot be deselected',
          'Physical Damage',
          'General Liability',
          'Cargo',
        ],
        component: 'ADD_INSURANCE_COVERAGE',
        behindTheScenes:
          'Limits and deductibles are fixed, never asked: $1M auto liability, $1M general liability, $100k cargo, with $1,000 deductibles on physical damage and cargo. Where Corgi is enabled, an attestation must be accepted before Submit unlocks.',
        asks: [{ text: 'What are the types of coverage you are looking for? Select all that apply.' }],
        edges: [answer('Picks coverages', 'SELECT_INDICATION')],
      },
    ],
  },
  {
    name: 'Estimates',
    action: 'IndicationAction',
    steps: [
      {
        service: 'SELECT_INDICATION',
        collected: 'Nothing — the quotes page takes over',
        component: 'none — redirects to /quotes',
        behindTheScenes:
          'Carriers are quoted in the background. The customer is sent to /quotes, which shows each carrier as it returns and handles checkout.',
        asks: [
          { text: 'We’re processing your data to create package options. This may take about a minute.' },
          { when: 'Packages are ready', text: 'Here are your coverage estimates!' },
        ],
        edges: [outcome('Continues on the quotes page', TERMINAL)],
      },
    ],
  },
]

/** Answered by the agent at any point, without moving the application on. */
export const SIDE_STEP: Step = {
  service: 'GENERAL_QUERY',
  collected: 'Message box + attachments',
  component: 'composer',
  asks: [{ text: 'Anything the customer types outside the current question.', editorial: true }],
  edges: [outcome('Answered by the agent', 'unchanged')],
}
